import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
} from '@material-ui/core';
import { Form } from '@unform/web';
import { isArray, isNaN, isNull, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import { Spinner, SelectDataInTable } from '~/components';
import {
  Select,
  Input,
  Checkbox,
  ComboboxMultiple,
  Autocomplete,
  CheckboxGroup,
} from '~/components/Form';
import {
  CONTRATO_ABASTECIMENTO,
  CENTRO_CUSTO_ABASTECIMENTO,
  CONTROLE_DE_COTAS,
  LUBRIFICACAO,
  SAP,
} from '~/constants/modules';
import { t, can, validCharacters } from '~/helpers';
import api from '~/services/api';
import {
  storeVeiculo,
  updateVeiculo,
} from '~/store/modules/locomotiva/actions';
import { changepostoBicosVazao } from '~/store/modules/locomotivaRegistration/actions';

import SelectPostoBicoDataInTable from '../components/SelectPostoBicoDataInTable';
import { Container, Wrapper } from './styles';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { formadigitacaofrota } = useSelector((state) => state.user.configs);
  const { loading, find, params } = useSelector((state) => state.locomotiva);

  const { postoBicosVazao } = useSelector(
    (state) => state.locomotivaRegistration
  );

  const [leituraC, setLeituraC] = useState(false);
  const [snPDLOC, setPDLOC] = useState(false);
  const [snPOVERFILL, setPOVERFILL] = useState(false);
  const [snPTTAG, setPTTAG] = useState(false);
  const [snDFTV, setDFTV] = useState(false);
  const [custo, setCusto] = useState(null);
  const [compartments, setCompartments] = useState([]);
  const [postosRestricao, setPostosRestricao] = useState([]);
  const [dataCompartments, setDataCompartments] = useState([]);
  const [postosVeiculo, setPostosVeiculo] = useState([]);
  const [snIdModeloVeiculo, setSnIdModeloVeiculo] = useState(null);
  const [nozzlerestrictions, setNozzlerestrictions] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(0);

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, []);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(
    () => () => {
      toggleLoading(false);
    },
    [toggleLoading]
  );

  const status = useMemo(
    () => [
      { value: 'N', label: t('s.geral.opcoes.nao') },
      { value: 'S', label: t('s.geral.opcoes.sim') },
    ],
    []
  );

  const tipomovimentaosap = useMemo(
    () => [
      { value: 201, label: t('s.cadastro.modeloveiculo.sap.201') },
      { value: 221, label: t('s.cadastro.modeloveiculo.sap.221') },
      { value: 261, label: t('s.cadastro.modeloveiculo.sap.261') },
      { value: 281, label: t('s.cadastro.modeloveiculo.sap.281') },
    ],
    []
  );

  const fieldsCompartimentos = useMemo(
    () => [
      {
        id: 'idcompartimento',
        label: t('s.cadastro.modeloveiculo.idcompartimento'),
        type: 'number',
        disabled: true,
      },
      {
        id: 'descricaocompartimento',
        label: t('s.cadastro.modeloveiculo.descricaocompartimento'),
        type: 'text',
        disabled: true,
      },
      {
        id: 'possuittagveiculocompartimento',
        label: t('s.cadastro.veiculo.possuittagveiculo'),
        type: 'select',
        options: status,
      },
      {
        id: 'leituracontinuattagveiculocompartimento',
        label: t('s.cadastro.veiculo.leituracontinuattagveiculo'),
        type: 'select',
        options: status,
      },
    ],
    [status]
  );

  const fieldsRestricaoBico = useMemo(
    () => [
      {
        id: 'codigonomeposto',
        label: t('s.menu.configuracao.posto'),
        type: 'select',
        options: postosRestricao,
      },
      {
        id: 'idbico',
        label: t('s.configuracao.bico.idbico'),
        type: 'select',
        options: postosVeiculo,
      },
      {
        id: 'vazaomaximalocomotivabicovazao',
        label: t('s.operacao.abastecimento.vazaomaximamovimentacao'),
        type: 'number',
      },
    ],
    [postosRestricao]
  );

  const schemaValidate = useMemo(
    () => ({
      possuittagveiculocompartimento: Yup.string()
        .nullable()
        .required(t('s.geral.validacao.campoobrigatorio')),
      leituracontinuattagveiculocompartimento: Yup.string().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
    }),
    []
  );

  const cellsCompartiments = useMemo(
    () => [
      {
        id: 'idcompartimento',
        label: t('s.cadastro.modeloveiculo.idcompartimento'),
      },
      {
        id: 'descricaocompartimento',
        label: t('s.cadastro.modeloveiculo.descricaocompartimento'),
      },
      {
        id: 'nomeabreviadoproduto',
        label: t('s.geral.camposistema.nomeproduto'),
      },
      {
        id: 'possuittagveiculocompartimento',
        label: t('s.cadastro.veiculo.possuittagveiculo'),
        format: (value) =>
          t(`s.geral.opcoes.${value && value === 'S' ? 'sim' : 'nao'}`),
      },
      {
        id: 'leituracontinuattagveiculocompartimento',
        label: t('s.cadastro.veiculo.leituracontinuattagveiculo'),
        format: (value) =>
          t(`s.geral.opcoes.${value && value === 'S' ? 'sim' : 'nao'}`),
      },
    ],
    []
  );

  const nozzleRestrictionschemaValidate = useMemo(
    () => ({
      idposto: Yup.number().required(t('s.geral.validacao.campoobrigatorio')),
      idbico: Yup.number().required(t('s.geral.validacao.campoobrigatorio')),
      vazaomaximaveiculobicovazao: Yup.number().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
    }),
    []
  );

  const cellsNozzleRestrictions = useMemo(
    () => [
      {
        id: 'codigonomeposto',
        label: t('s.menu.configuracao.posto'),
      },
      {
        id: 'idbico',
        label: t('s.configuracao.bico.idbico'),
      },
      {
        id: 'vazaomaximaveiculobicovazao',
        label: t('s.operacao.abastecimento.vazaomaximamovimentacao'),
      },
    ],
    []
  );

  const initials = create
    ? { bloqueiaabastacimatanqueveiculo: 'S', ...data }
    : { ...find, ...data };

  async function handleValidateCenterCost() {
    await api
      .get(`centroscustos/${custo}`, { params: { pesquisalista: true } })
      .catch(async () => {
        await reference.current.setFieldValue('idcentrocusto', null);
        await setCusto(null);

        reference.current.setFieldError(
          'idcentrocusto',
          'Centro de Custo não encontrado!'
        );
      });
  }

  async function handleSubmit(param) {
    try {
      if (pendingRequests > 0) {
        toast.warning(
          'Aguarde o carregamento completo dos dados do formulário.'
        );
        return;
      }

      const schema = Yup.object().shape({
        idveiculo: create
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                (value) => validCharacters(value)
              )
          : Yup.string(),
        idempresaveiculo: create
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .nullable()
              .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                (value) => validCharacters(value)
              )
          : Yup.string(),
        idmodeloveiculo: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        statusveiculo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        bitolaveiculo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        tipolocomotivaveiculo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        vazaomaximaveiculo: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        volumetanqueveiculo: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            50001,
            t('s.geral.validacao.valormaximo').replace('%s', 50000)
          ),
        observacaoveiculo: Yup.string()
          .nullable()
          .max(255, t('s.geral.validacao.tamanhomaximo').replace('%s', 255)),
        bloqueiaabastacimatanqueveiculo: Yup.string().nullable(),
        iddvecveiculo: Yup.string()
          .nullable()
          .typeError('Erro')
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', '20')),
        possuidvecveiculo: Yup.string().nullable(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        idveiculo: '',
        tipoveiculo: 5, // locomotiva
        totalizadorveiculo: 0,
        statusdbic4veiculo: 1,
        transponders: [],
        cota: {
          controlacotaveiculocota: 'N',
          volumeveiculocota: 0,
          saldoveiculocota: 0.0,
          anomesveiculocota: '',
        },
        sap: {
          movimentoveiculosap: 201,
          sigordemveiculosap: null,
          elementopepveiculosap: null,
          contarazaoveiculosap: null,
          diagramaredeveiculosap: null,
          reservaveiculosap: null,
          equiptosapveiculosap: null,
          frotasapveiculosap: null,
          operacaoveiculosap: null,
        },
        ...initials,
        ...param,
        digitostotalizadorveiculo: 0,
        casasdecimaistotalizadorveiculo: 0,
        possuidvecveiculo: param.possuidvecveiculo ? '1' : '0',
        tipodvecveiculo: 0,
        possuittagveiculo: snPTTAG ? 'S' : 'N',
        digitafrotaterminalveiculo: snDFTV ? 1 : 0,
        idcliente: client,
        veiculocompartimento: create
          ? compartments.map((c) => ({ ...c, idveiculo: param.idveiculo }))
          : compartments,
        postos: postosVeiculo,
        leituracontinuattagveiculo: param.leituracontinuattagveiculo || 0,
        placaveiculo: '',
        idpostoproprietario: null,
        idpostogestor: null,
        maxtagveiculo: param.maxtagveiculo || null,
        tipoauxiliarveiculo: null,
        idcontrato: param.idcontrato || null,
        bloqueiaabastacimatanqueveiculo: null,
        rodagemminimaveiculo: 0,
        rodagemmaximaveiculo: 0,
        tipototalizadorveiculo: 0,
        aceitatotforafaixa: 'N',
        possuidlocveiculo: snPDLOC ? 'S' : 'N',
        possuioverfillveiculo: snPOVERFILL ? 'S' : 'N',
        veiculobicosvazoes: postoBicosVazao.map((itempostobico) => ({
          idcliente: itempostobico.idcliente,
          idveiculo: itempostobico.idveiculo,
          idposto: itempostobico.idposto,
          codigonomeposto: itempostobico.codigonomeposto,
          idbico: itempostobico.idbico,
          vazaomaxima: itempostobico.vazaomaxima,
        })),
      };

      if (create) {
        dispatch(storeVeiculo(objSend, params));
      } else {
        dispatch(updateVeiculo(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  const handleGetPostos = useCallback(async () => {
    try {
      handleAsyncLoadingChange(true);
      const response = await api.get('postos', {
        params: {
          pagesize: 9999,
          orderfield: 'codigonomeposto',
          ordination: 'asc',
          pesquisalista: true,
        },
        paramsSerializer: (param) => qs.stringify(param),
      });

      setPostosRestricao(
        response.data.items.map((posto) => ({
          value: posto.idposto,
          label: posto.codigonomeposto,
        }))
      );
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      handleAsyncLoadingChange(false);
    }
  }, [handleAsyncLoadingChange]);

  const handleGetCompartiments = useCallback(async (id) => {
    if (can(LUBRIFICACAO)) {
      try {
        handleAsyncLoadingChange(true);
        if (isNull(id)) {
          setDataCompartments([]);
        } else {
          const response = await api.get(`veiculoscompartimentos/${id}`, {
            params: { pagesize: 99999, pesquisalista: true },
          });

          setDataCompartments(response.data);
        }
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast(t(err));
      } finally {
        handleAsyncLoadingChange(false);
      }
    }
  }, [handleAsyncLoadingChange]);

  function handleMaxTagVeiculoArray(quantTag) {
    const tagsLocal = [];
    if (quantTag) {
      for (let i = 0; i < quantTag; i++) {
        if (i === 0) {
          const item = {
            label: 'Não Definido',
            value: 0,
          };
          tagsLocal.push(item);
        } else {
          const item = {
            label: String(i),
            value: i,
          };
          tagsLocal.push(item);
        }
      }
    }
  }

  async function handleModeloVeiculo(value) {
    await handleGetCompartiments(value.idmodeloveiculo);
    await handleGetPostos();
    await handleMaxTagVeiculoArray(value.maxtagveiculomodelo);
    await setSnIdModeloVeiculo(value.idmodeloveiculo);
  }

  useEffect(() => {
    if (find) {
      const {
        possuittagveiculo,
        veiculocompartimento,
        digitafrotaterminalveiculo,
        postos,
        possuidlocveiculo,
        possuioverfillveiculo,
        veiculobicosvazoes,
      } = find;

      if (possuidlocveiculo && possuidlocveiculo === 'S') {
        setPDLOC(true);
      }

      if (possuioverfillveiculo && possuioverfillveiculo === 'S') {
        setPOVERFILL(true);
      }

      if (possuittagveiculo && possuittagveiculo === 'S') {
        setPTTAG(true);
        setLeituraC(true);
      }

      if (veiculocompartimento) {
        setCompartments(veiculocompartimento);
      }

      if (postos) {
        setPostosVeiculo(postos);
      }

      if (digitafrotaterminalveiculo) {
        setDFTV(true);
      }

      if (veiculobicosvazoes) {
        if (!isEmpty(veiculobicosvazoes))
          dispatch(
            changepostoBicosVazao(
              veiculobicosvazoes.map((item) => ({
                ...item,
              }))
            )
          );
        // setNozzlerestrictions(veiculobicosvazoes);
      }
    }
  }, [find]);

  const handleChangePostoBicosVazao = useCallback((value) => {
    dispatch(changepostoBicosVazao(value));
  }, []);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <ExpansionPanel expanded>
            <ExpansionPanelSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.veiculo.informacoeslocomotiva')}
              </Typography>
            </ExpansionPanelSummary>

            <ExpansionPanelDetails>
              <Container>
                <Input
                  name="idveiculo"
                  label={t('s.cadastro.veiculo.locomotiva')}
                  disabled={!create}
                />

                <Autocomplete
                  name="idempresaveiculo"
                  label="nomeempresaveiculo"
                  url="empresasveiculos"
                  unique="idempresaveiculo"
                  title={t('s.cadastro.veiculo.idempresaveiculo')}
                  onLoadingChange={handleAsyncLoadingChange}
                />

                {can(CENTRO_CUSTO_ABASTECIMENTO) && (
                  <Autocomplete
                    name="idcentrocusto"
                    label="nomecentrocustostr"
                    unique="idcentrocusto"
                    title={t('s.cadastro.veiculo.idcentrocusto')}
                    url="centroscustos"
                    onLoadingChange={handleAsyncLoadingChange}
                  />
                )}

                <Autocomplete
                  name="idmodeloveiculo"
                  title={t('s.cadastro.veiculo.idmodeloveiculo')}
                  label="nomemodelomarcamodeloveiculo"
                  unique="idmodeloveiculo"
                  url="modelosveiculos"
                  params={{
                    fieldlist:
                      'idmodeloveiculo,nomemodelomarcamodeloveiculo,maxtagveiculomodelo',
                    orderfield: 'nomemodelomarcamodeloveiculo',
                    wherefield: ['tipomodeloveiculo'],
                    valuewherefield: ['L'],
                    typewherefield: ['EQUALS'],
                  }}
                  getValue={(value) => {
                    handleModeloVeiculo(value);
                  }}
                  onLoadingChange={handleAsyncLoadingChange}
                />

                {snIdModeloVeiculo !== null && (
                  <Autocomplete
                    name="maxtagveiculo"
                    title={t('s.cadastro.veiculo.maxtagveiculo')}
                    label="nomemaxtagveiculo"
                    unique="maxtagveiculo"
                    url="modelosveiculosmaxtag"
                    params={{
                      fieldlist:
                        'idmodeloveiculo,nomemodelomarcamodeloveiculo,maxtagveiculomodelo',
                      orderfield: 'nomemodelomarcamodeloveiculo',
                      wherefield: 'idmodeloveiculo',
                      valuewherefield: snIdModeloVeiculo,
                      typewherefield: 'EQUALS',
                    }}
                    getValue={(value) => {
                      // handleModeloVeiculo(value);
                    }}
                    onLoadingChange={handleAsyncLoadingChange}
                  />
                )}

                {can(CONTRATO_ABASTECIMENTO) && (
                  <Autocomplete
                    name="idcontrato"
                    label="nomecontratostr"
                    unique="idcontrato"
                    title={t('s.cadastro.veiculo.idcontrato')}
                    url="contratos"
                    onLoadingChange={handleAsyncLoadingChange}
                  />
                )}

                <Select
                  name="bitolaveiculo"
                  label={t('s.cadastro.veiculo.bitolaveiculo')}
                  options={[
                    {
                      value: 'L',
                      label: t('s.cadastro.veiculo.bitolaveiculo.largo'),
                    },
                    {
                      value: 'E',
                      label: t('s.cadastro.veiculo.bitolaveiculo.estreito'),
                    },
                  ]}
                />

                <Input
                  name="vazaomaximaveiculo"
                  label={t('s.operacao.abastecimento.vazaomaximamovimentacao')}
                  type="number"
                />

                <Select
                  name="tipolocomotivaveiculo"
                  label={t('s.cadastro.veiculo.tipolocomotivaveiculo')}
                  options={[
                    { value: 'L', label: t('s.geral.opcoes.locomotiva') },
                    { value: 'O', label: t('s.geral.opcoes.outros') },
                  ]}
                />
                <Select
                  name="statusveiculo"
                  label={t('s.cadastro.veiculo.statusveiculo')}
                  options={[
                    { value: 'L', label: t('s.geral.opcoes.liberado') },
                    { value: 'B', label: t('s.geral.opcoes.bloqueado') },
                    { value: 'C', label: t('s.geral.opcoes.cancelado') },
                  ]}
                />

                <Input
                  multiline
                  name="observacaoveiculo"
                  label={t('s.operacao.limpezatag.observacaohistoricotag')}
                  rows={4}
                />
              </Container>
            </ExpansionPanelDetails>

            <ExpansionPanelSummary
              aria-controls="panel2a-content"
              id="panel2a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>{t('s.cadastro.veiculo.equipamentos')}</Typography>
            </ExpansionPanelSummary>

            <ExpansionPanelDetails>
              <Container>
                <Checkbox
                  name="possuidlocveiculo"
                  label={t('s.cadastro.veiculo.possuidlocveiculo')}
                  checked={initials.possuidlocveiculo === 'S'}
                  getValue={(value) => {
                    setPDLOC(value);
                  }}
                />

                <Checkbox
                  name="possuioverfillveiculo"
                  label={t('s.cadastro.veiculo.possuioverfillveiculo')}
                  checked={initials.possuioverfillveiculo === 'S'}
                  getValue={(value) => {
                    setPOVERFILL(value);
                  }}
                />

                <Checkbox
                  name="possuidvecveiculo"
                  label={t('s.cadastro.veiculo.possuia2vf')}
                  checked={initials.possuidvecveiculo === '1'}
                />

                <Checkbox
                  name="possuittagveiculo"
                  label={t('s.cadastro.veiculo.possuittagveiculo')}
                  getValue={(value) => {
                    setLeituraC(value);
                    setPTTAG(value);
                  }}
                />

                <br />

                {Number(formadigitacaofrota) === 1 && (
                  <Checkbox
                    name="digitafrotaterminalveiculo"
                    label={t('s.cadastro.veiculo.digitafrotaterminalveiculo')}
                    disabled={!leituraC}
                    getValue={(value) => setDFTV(value)}
                  />
                )}

                <Select
                  name="leituracontinuattagveiculo"
                  label={t('s.geral.nivelseguranca')}
                  options={[
                    { value: 0, label: t('s.geral.nivelseguranca.semleitura') },
                    { value: 1, label: t('s.geral.nivelseguranca.baixo') },
                    { value: 2, label: t('s.geral.nivelseguranca.medio') },
                    { value: 3, label: t('s.geral.nivelseguranca.alto') },
                  ]}
                  defaultValue={initials.leituracontinuattagveiculo || 0}
                />

                <Input
                  name="iddvecveiculo"
                  label={t('s.cadastro.locomotiva.a2vflocomotiva')}
                  disabled="true"
                />

                <ComboboxMultiple
                  name="transponders"
                  title={t('s.cadastro.veiculo.transponders')}
                  defaultValue={initials?.transponders || []}
                  disabled
                />
              </Container>
            </ExpansionPanelDetails>

            <ExpansionPanelSummary
              aria-controls="panel3a-content"
              id="panel3a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>{t('s.cadastro.veiculo.autonomia')}</Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <Input
                  name="volumetanqueveiculo"
                  label={t('s.cadastro.veiculo.volumetanque')}
                  type="number"
                />
              </Container>
            </ExpansionPanelDetails>

            <ExpansionPanelSummary
              aria-controls="panel4a-content"
              id="panel4a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.veiculo.produtosabastecer')}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <ComboboxMultiple
                  name="produtos"
                  label="nomeproduto"
                  url="produtos"
                  unique="idproduto"
                  title={t('s.cadastro.veiculo.produtos')}
                  defaultValue={initials?.produtos || []}
                  params={{
                    wherefield: ['tipoproduto'],
                    typewherefield: ['EQUALS'],
                    valuewherefield: ['C'],
                    pesquisalista: ['true'],
                  }}
                  onLoadingChange={handleAsyncLoadingChange}
                />
              </Container>
            </ExpansionPanelDetails>

            {can(CONTROLE_DE_COTAS) && (
              <>
                <ExpansionPanelSummary
                  aria-controls="panel5a-content"
                  id="panel5a-header"
                  style={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    borderBottom: '1px solid rgba(0,0,0,.2)',
                  }}
                >
                  <Typography>{t('s.cadastro.veiculo.cota')}</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                  <Container>
                    <Select
                      name="cota.controlacotaveiculocota"
                      label={t('s.cadastro.veiculo.controlacotaveiculocota')}
                      options={status}
                      defaultValue={
                        initials.cota
                          ? initials.cota.controlacotaveiculocota
                          : 'N'
                      }
                    />

                    <Input
                      name="cota.volumeveiculocota"
                      label={t('s.cadastro.veiculo.volumeveiculocota')}
                      type="number"
                      min="0"
                      defaultValue={
                        initials.cota ? initials.cota.volumeveiculocota : 0
                      }
                    />

                    <Input
                      name="cota.saldoveiculocota"
                      label={t('s.cadastro.veiculo.saldoveiculocota')}
                      type="number"
                      disabled
                    />
                  </Container>
                </ExpansionPanelDetails>
              </>
            )}

            {can(LUBRIFICACAO) && (
              <>
                <ExpansionPanelSummary
                  aria-controls="panel6a-content"
                  id="panel6a-header"
                  style={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    borderBottom: '1px solid rgba(0,0,0,.2)',
                  }}
                >
                  <Typography>
                    {t('s.cadastro.modeloveiculo.compartimento')}
                  </Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                  <Container>
                    <SelectDataInTable
                      icons={false}
                      create={false}
                      remove={false}
                      checkbox={false}
                      showPagination={false}
                      size="small"
                      variant="contained"
                      position="row"
                      onChanged={(value) =>
                        setCompartments(
                          value.map((v) => ({
                            ...v,
                            leituracontinuattagveiculocompartimento:
                              v.leituracontinuattagveiculocompartimento || 'N',
                            possuittagveiculocompartimento:
                              v.possuittagveiculocompartimento || 'N',
                          }))
                        )
                      }
                      unique="idcompartimento"
                      maxWidth="xs"
                      initials={
                        initials.veiculocompartimento || dataCompartments
                      }
                      fields={fieldsCompartimentos}
                      schema={schemaValidate}
                      cells={cellsCompartiments}
                      titleModal={t('s.cadastro.modeloveiculo.compartimento')}
                    />
                  </Container>
                </ExpansionPanelDetails>
              </>
            )}

            <ExpansionPanelSummary
              aria-controls="panel6a-content"
              id="panel6a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.locomotiva.restricaobico')}
              </Typography>
            </ExpansionPanelSummary>

            <ExpansionPanelDetails>
              <Container>
                <SelectPostoBicoDataInTable
                  noUpdate
                  // checkbox={false}
                  showPagination={false}
                  paginate={99999}
                  size="small"
                  variant="contained"
                  position="row"
                  unique={['idposto', 'idbico']}
                  maxWidth="xs"
                  initials={postoBicosVazao}
                  onChanged={handleChangePostoBicosVazao}
                  titleModal={t('s.cadastro.locomotiva.restricaobico')}
                />
              </Container>
            </ExpansionPanelDetails>

            {can(SAP) && (
              <>
                <ExpansionPanelSummary
                  aria-controls="panel6a-content"
                  id="panel6a-header"
                  style={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    borderBottom: '1px solid rgba(0,0,0,.2)',
                  }}
                >
                  <Typography>{t('s.cadastro.modeloveiculo.sap')}</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                  <Container>
                    <Select
                      name="sap.movimentoveiculosap"
                      label={t('s.cadastro.veiculo.sap.movimentoveiculosap')}
                      options={tipomovimentaosap}
                      defaultValue={
                        initials.sap ? initials.sap.movimentoveiculosap : 201
                      }
                    />

                    <Input
                      name="sap.sigordemveiculosap"
                      label={t('s.cadastro.veiculo.sap.sigordemveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.sigordemveiculosap : null
                      }
                      inputProps={{ maxLength: 20 }}
                    />

                    <Input
                      name="sap.elementopepveiculosap"
                      label={t('s.cadastro.veiculo.sap.elementopepveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.elementopepveiculosap : null
                      }
                      inputProps={{ maxLength: 24 }}
                    />

                    <Input
                      name="sap.contarazaoveiculosap"
                      label={t('s.cadastro.veiculo.sap.contarazaoveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.contarazaoveiculosap : null
                      }
                      inputProps={{ maxLength: 10 }}
                    />

                    <Input
                      name="sap.diagramaredeveiculosap"
                      label={t('s.cadastro.veiculo.sap.diagramaredeveiculosap')}
                      defaultValue={
                        initials.sap
                          ? initials.sap.diagramaredeveiculosap
                          : null
                      }
                      inputProps={{ maxLength: 12 }}
                    />

                    <Input
                      name="sap.reservaveiculosap"
                      label={t('s.cadastro.veiculo.sap.reservaveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.reservaveiculosap : null
                      }
                      inputProps={{ maxLength: 10 }}
                    />

                    <Input
                      name="sap.equiptosapveiculosap"
                      label={t('s.cadastro.veiculo.sap.equiptosapveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.equiptosapveiculosap : null
                      }
                      inputProps={{ maxLength: 18 }}
                    />

                    <Input
                      name="sap.frotasapveiculosap"
                      label={t('s.cadastro.veiculo.sap.frotasapveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.frotasapveiculosap : null
                      }
                      inputProps={{ maxLength: 23 }}
                    />

                    <Input
                      name="sap.operacaoveiculosap"
                      label={t('s.cadastro.veiculo.sap.operacaoveiculosap')}
                      defaultValue={
                        initials.sap ? initials.sap.operacaoveiculosap : null
                      }
                      inputProps={{ maxLength: 4 }}
                    />
                  </Container>
                </ExpansionPanelDetails>
              </>
            )}

            <ExpansionPanelSummary
              aria-controls="panel6a-content"
              id="panel6a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>{t('s.geral.postocomboio')}</Typography>
            </ExpansionPanelSummary>

            <ExpansionPanelDetails>
              <Container>
                <CheckboxGroup
                  search
                  name="postos"
                  title={t('s.cadastro.veiculo.acessotodospostos')}
                  label="codigonomeposto"
                  unique="idposto"
                  url="postoscomboios"
                  getValue={(value) => {
                    setPostosVeiculo(value);
                  }}
                  query={{
                    filteruser: false,
                    wherefield: 'statusposto',
                    valuewherefield: 'L',
                    typewherefield: 'EQUALS',
                  }}
                  onLoadingChange={handleAsyncLoadingChange}
                />
              </Container>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
};
